import { showDeleteConfirmation } from '@modules/common';
import { switchMap } from 'rxjs/operators';
import { BaseBloc } from '@core/utils/bloc';
import { Repository } from '@core/utils/repository';
import { handleMessage, requestMessage } from '@business/messages';
import { CommissionOverrideResponse, CommissionOverrideId, CommissionGroupRequest } from '@business/entities/commission_group';
import { CommissionOverrideAPI } from '@business/api/commission_override';

export class CommissionOverrideBloc extends BaseBloc {
  private readonly commissionOverrideRepo = new Repository<CommissionOverrideResponse>({
    getItemId: group => group.id,
  });

  readonly commissionOverride$ = this.commissionOverrideRepo.items$;
  readonly selectCommissionOverride = this.commissionOverrideRepo.selectItem;

  onReset() {
    this.commissionOverrideRepo.reset();
  }

  fetchCommissionOverrides = () => {
    return CommissionOverrideAPI.fetchCommissionOverrides().pipe(
      this.commissionOverrideRepo.ops.reset(),
      handleMessage({ error: requestMessage('fetch_commission_groups_error') }),
    );
  };

  fetchCommissionOverride = (id: CommissionOverrideId) => {
    return CommissionOverrideAPI.fetchCommissionOverride(id).pipe(
      this.commissionOverrideRepo.ops.upsertOne(item => ({ item })),
      handleMessage({ error: requestMessage('fetch_commission_groups_error') }),
    );
  };

  createCommissionOverride = (commissionGroup: Partial<CommissionOverrideResponse>) => {
    return CommissionOverrideAPI.createCommissionOverride(commissionGroup).pipe(
      this.commissionOverrideRepo.ops.addOne(item => item),
      handleMessage({
        type: requestMessage('create_commission_group'),
      }),
    );
  };

  updateCommissionOverride = (id: CommissionOverrideId, commissionGroup: Partial<CommissionGroupRequest>) => {
    return CommissionOverrideAPI.updateCommissionOverride(id, commissionGroup).pipe(
      this.commissionOverrideRepo.ops.upsertOne(item => ({ item })),
      handleMessage({
        type: requestMessage('update_commission_group'),
      }),
    );
  };

  deleteCommissionOverride = (id: CommissionOverrideId) => {
    return showDeleteConfirmation('Delete CommissionGroups', 'Do you really want to remove this commissionGroup?').pipe(
      switchMap(() => CommissionOverrideAPI.deleteCommissionOverride(id)),
      this.commissionOverrideRepo.ops.removeOne(() => id),
      handleMessage({
        type: requestMessage('delete_commission_group'),
      }),
    );
  };
}
